import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  TerminalBlockAssignment,
  getTerminalBlockAssignments, 
  createTerminalBlockAssignment, 
  deleteTerminalBlockAssignment,
  getAvailableTerminalSpecifications,
  getNextPosition,
  createDeviceInstanceFromSpecification,
  updateDeviceInstance,
  batchUpdatePositions
} from '../api/terminalBlockAssignments'
import { getTerminalBlock } from '../api/terminalInstances'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface TerminalInstancesPageProps {}

// Sortable row component
function SortableRow({ 
  assignment, 
  selectedAssignments, 
  handleSelectAssignment, 
  handleEditAssignment, 
  handleDeleteAssignment 
}: {
  assignment: TerminalBlockAssignment
  selectedAssignments: Set<number>
  handleSelectAssignment: (id: number) => void
  handleEditAssignment: (assignment: TerminalBlockAssignment) => void
  handleDeleteAssignment: (id: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: assignment.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'bg-blue-50' : ''}>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <input
          type="checkbox"
          checked={selectedAssignments.has(assignment.id)}
          onChange={() => handleSelectAssignment(assignment.id)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {assignment.device_instance_id}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        {assignment.position_in_block}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {assignment.position_label || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {assignment.specification_name || '-'}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        {assignment.description || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={() => handleEditAssignment(assignment)}
          className="text-blue-600 hover:text-blue-900 mr-3"
        >
          ✏️
        </button>
        <button
          onClick={() => handleDeleteAssignment(assignment.id)}
          className="text-red-600 hover:text-red-900 mr-3"
        >
          🗑️
        </button>
        <button
          {...listeners}
          {...attributes}
          className="text-gray-400 hover:text-gray-600 cursor-move"
          title="Перетащить для изменения позиции"
        >
          ⋮⋮
        </button>
      </td>
    </tr>
  )
}

export default function TerminalInstancesPage({}: TerminalInstancesPageProps) {
  const { projectId, panelId, blockId } = useParams<{
    projectId: string
    panelId: string
    blockId: string
  }>()
  const queryClient = useQueryClient()

  // States
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState<TerminalBlockAssignment | null>(null)
  const [selectedSpecificationId, setSelectedSpecificationId] = useState<number | null>(null)
  const [quantity, setQuantity] = useState<number>(1)
  const [editPositionLabel, setEditPositionLabel] = useState<string>('')
  const [editDescription, setEditDescription] = useState<string>('')
  const [selectedAssignments, setSelectedAssignments] = useState<Set<number>>(new Set())
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const [sortedAssignments, setSortedAssignments] = useState<TerminalBlockAssignment[]>([])

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Get terminal block info
  const { data: terminalBlock } = useQuery({
    queryKey: ['terminal-block', blockId],
    queryFn: () => getTerminalBlock(Number(blockId)),
    enabled: !!blockId
  })

  // Get terminal block assignments for this block
  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['terminal-block-assignments', blockId],
    queryFn: () => getTerminalBlockAssignments(Number(blockId)),
    enabled: !!blockId
  })

  // Get available terminal specifications
  const { data: availableSpecifications = [] } = useQuery({
    queryKey: ['terminal-specifications', projectId],
    queryFn: () => getAvailableTerminalSpecifications(Number(projectId)),
    enabled: !!projectId
  })

  // Sync sorted assignments with fetched assignments
  useEffect(() => {
    if (assignments.length > 0) {
      const sorted = [...assignments].sort((a, b) => a.position_in_block - b.position_in_block)
      setSortedAssignments(sorted)
    }
  }, [assignments])


  // Create assignment mutation
  const createAssignmentMutation = useMutation({
    mutationFn: createTerminalBlockAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-block-assignments', blockId] })
      queryClient.invalidateQueries({ queryKey: ['terminal-specifications', projectId] })
      setShowCreateForm(false)
      setSelectedSpecificationId(null)
    }
  })

  // Update device instance mutation
  const updateDeviceInstanceMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number, updates: any }) => updateDeviceInstance(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-block-assignments', blockId] })
      queryClient.invalidateQueries({ queryKey: ['terminal-specifications', projectId] })
      setShowEditForm(false)
      setEditingAssignment(null)
      setEditPositionLabel('')
      setEditDescription('')
    }
  })

  // Delete assignment mutation
  const deleteAssignmentMutation = useMutation({
    mutationFn: deleteTerminalBlockAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-block-assignments', blockId] })
      queryClient.invalidateQueries({ queryKey: ['terminal-specifications', projectId] })
    }
  })

  // Batch update positions mutation
  const batchUpdatePositionsMutation = useMutation({
    mutationFn: batchUpdatePositions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-block-assignments', blockId] })
    }
  })

  // Get next available position
  const getNextAvailablePosition = async () => {
    return await getNextPosition(Number(blockId))
  }

  // Handle create assignment (supports multiple instances)
  const handleCreateAssignment = async () => {
    if (!selectedSpecificationId) return
    
    try {
      let currentPosition = await getNextAvailablePosition()
      
      // Create assignments for each quantity
      for (let i = 0; i < quantity; i++) {
        // Create new device instance from specification
        const newDeviceInstance = await createDeviceInstanceFromSpecification(selectedSpecificationId, Number(panelId), Number(projectId))
        
        // Create assignment for the new device instance
        await new Promise((resolve, reject) => {
          createAssignmentMutation.mutate({
            terminal_block_id: Number(blockId),
            device_instance_id: newDeviceInstance.id,
            position_in_block: currentPosition + i
          }, {
            onSuccess: resolve,
            onError: reject
          })
        })
      }
      
      // Reset form after all assignments created
      handleCloseModals()
    } catch (error) {
      console.error('Failed to create assignments:', error)
    }
  }

  // Handle edit assignment
  const handleEditAssignment = (assignment: TerminalBlockAssignment) => {
    setEditingAssignment(assignment)
    setEditPositionLabel(assignment.position_label || '')
    setEditDescription(assignment.description || '')
    setShowEditForm(true)
  }

  // Handle update assignment
  const handleUpdateAssignment = () => {
    if (!editingAssignment) return
    
    updateDeviceInstanceMutation.mutate({
      id: editingAssignment.device_instance_id,
      updates: {
        position_label: editPositionLabel,
        description: editDescription
      }
    })
  }

  // Handle delete assignment
  const handleDeleteAssignment = (id: number) => {
    if (confirm('Удалить экземпляр клеммника?')) {
      deleteAssignmentMutation.mutate(id)
    }
  }

  // Bulk selection handlers
  const handleSelectAssignment = (id: number) => {
    setSelectedAssignments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedAssignments.size === assignments.length) {
      setSelectedAssignments(new Set())
    } else {
      setSelectedAssignments(new Set(assignments.map(assignment => assignment.id)))
    }
  }

  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedAssignments.size === 0) return
    setShowBulkDeleteConfirm(true)
  }

  const confirmBulkDelete = async () => {
    try {
      const deletePromises = Array.from(selectedAssignments).map(id => 
        deleteAssignmentMutation.mutateAsync(id)
      )
      
      await Promise.all(deletePromises)
      setSelectedAssignments(new Set())
      setShowBulkDeleteConfirm(false)
    } catch (error) {
      console.error('Failed to delete assignments:', error)
    }
  }

  // Handle drag end - reorder and update positions
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = sortedAssignments.findIndex((a) => a.id === active.id)
    const newIndex = sortedAssignments.findIndex((a) => a.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Reorder array
    const reordered = arrayMove(sortedAssignments, oldIndex, newIndex)
    
    // Reassign position_in_block sequentially
    const updates = reordered.map((assignment, index) => ({
      id: assignment.id,
      position_in_block: index + 1
    }))

    // Optimistically update UI
    setSortedAssignments(reordered.map((assignment, index) => ({
      ...assignment,
      position_in_block: index + 1
    })))

    // Save to backend
    try {
      await batchUpdatePositionsMutation.mutateAsync(updates)
    } catch (error) {
      console.error('Failed to update positions:', error)
      // Revert on error
      queryClient.invalidateQueries({ queryKey: ['terminal-block-assignments', blockId] })
    }
  }

  // Close modal handlers
  const handleCloseModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setEditingAssignment(null)
    setSelectedSpecificationId(null)
    setQuantity(1) // Reset quantity to default
    setEditPositionLabel('')
    setEditDescription('')
    setShowBulkDeleteConfirm(false)
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Загрузка...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to={`/projects/${projectId}`} className="hover:text-blue-600">
              Проект
            </Link>
            <span>→</span>
            <Link to={`/projects/${projectId}/panels/${panelId}`} className="hover:text-blue-600">
              Панель
            </Link>
            <span>→</span>
            <Link to={`/projects/${projectId}/panels/${panelId}/terminal-blocks`} className="hover:text-blue-600">
              Блоки клеммников
            </Link>
            <span>→</span>
            <span>Экземпляры</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Экземпляры клеммников: {terminalBlock?.name || `Блок ${blockId}`}
          </h1>
          <p className="text-gray-600 mt-1">
            Управление экземплярами клеммников в блоке
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Добавить экземпляр
        </button>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Экземпляры клеммников ({assignments.length})
            </h2>
            {selectedAssignments.size > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  Выбрано: {selectedAssignments.size}
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-600 text-white px-3 py-1.5 rounded text-sm hover:bg-red-700 transition-colors"
                >
                  🗑️ Удалить выбранное
                </button>
              </div>
            )}
          </div>
        </div>

        {assignments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-500 mb-4">Нет экземпляров клеммников</div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="text-blue-600 hover:text-blue-700"
            >
              Добавить первый экземпляр
            </button>
          </div>
        ) : (
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedAssignments.size === assignments.length && assignments.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Позиция
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Позиционное обозначение
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Спецификация
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Описание
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  </tr>
                </thead>
                <SortableContext 
                  items={sortedAssignments.map(a => a.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedAssignments.map((assignment) => (
                      <SortableRow
                        key={assignment.id}
                        assignment={assignment}
                        selectedAssignments={selectedAssignments}
                        handleSelectAssignment={handleSelectAssignment}
                        handleEditAssignment={handleEditAssignment}
                        handleDeleteAssignment={handleDeleteAssignment}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </div>
          </DndContext>
        )}
      </div>

      {/* Create Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Добавить экземпляр клеммника</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Тип клеммника
                </label>
                <select
                  value={selectedSpecificationId || ''}
                  onChange={(e) => setSelectedSpecificationId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Выберите тип клеммника</option>
                  {availableSpecifications.map((spec) => (
                    <option key={spec.id} value={spec.id}>
                      {spec.name} ({spec.device_type})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Количество экземпляров
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите количество"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Будет создано {quantity} экземпляр{quantity === 1 ? '' : quantity < 5 ? 'а' : 'ов'} в последовательных позициях
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCloseModals}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateAssignment}
                disabled={!selectedSpecificationId || createAssignmentMutation.isPending}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {createAssignmentMutation.isPending ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditForm && editingAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Редактировать экземпляр клеммника</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Позиция в блоке: {editingAssignment.position_in_block}
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Позиционное обозначение
                </label>
                <input
                  type="text"
                  value={editPositionLabel}
                  onChange={(e) => setEditPositionLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите позиционное обозначение"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите описание"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCloseModals}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Отмена
              </button>
              <button
                onClick={handleUpdateAssignment}
                disabled={updateDeviceInstanceMutation.isPending}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateDeviceInstanceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Подтвердите удаление</h3>
            
            <p className="text-gray-700 mb-6">
              Вы уверены, что хотите удалить <strong>{selectedAssignments.size}</strong> экземпляр
              {selectedAssignments.size === 1 ? '' : selectedAssignments.size < 5 ? 'а' : 'ов'} клеммников?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={deleteAssignmentMutation.isPending}
              >
                Отмена
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={deleteAssignmentMutation.isPending}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteAssignmentMutation.isPending ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 